import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { CargoSpace, LoadingSpaceType } from '../../../../core/types';
import { Project } from '../../../../core/objects/project';
import { ProjectService } from '../../../../core/services/project.service';
import { Subscription } from 'rxjs';

interface VehicleLoadingForm extends UntypedFormGroup {
  value: {
    palletVehicleToLoad: boolean;
    palletVehicle: string;
  };
}

@Component({
    selector: 'gl-loading-space-pallet-vehicle-loading',
    templateUrl: './loading-space-pallet-vehicle-loading.component.html',
    styleUrls: ['./loading-space-pallet-vehicle-loading.component.scss'],
    standalone: false
})
export class LoadingSpacePalletVehicleLoadingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isEdit = false;
  @Input() public loadingSpace: LoadingSpaceType;
  @Output() public submitEvent = new EventEmitter();
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  public form: VehicleLoadingForm;
  public isPalletVehicle = false;
  public workSpaces: LoadingSpaceType[];
  public choosenWorkspace: LoadingSpaceType = null;
  public isSelectOpen = false;
  private formSubscription: Subscription;
  public palletVehicleCurrentValue: number = null;
  public cargoSpaces: CargoSpace[][];

  constructor(private projectService: ProjectService) {
    this.form = this.generateLoadingForm();
    this.projectService.project.subscribe((project: Project) => {
      if (project.workSpaces.length === 0) {
        this.workSpaces = [];
        return;
      }
      this.cargoSpaces = [...project.workSpaces]
        .map((workspace) => workspace.cargoSpaces)
        .filter((workspace) => workspace[0].type !== 3);
      if (this.cargoSpaces.length > 0) {
        this.workSpaces = this.cargoSpaces.map((value) => ({
          ...value[0],
          ...value[0].dimensions,
        })) as unknown as LoadingSpaceType[];
      } else {
        this.form.disable();
      }
    });
  }

  public onChosenSelect(event: LoadingSpaceType): void {
    this.choosenWorkspace = event;
    this.isSelectOpen = false;
    this.form.get('palletVehicle').patchValue(event.id);
    this.submitEvent.emit(this.form.value);
  }

  public onChosenValue(): void {
    if (!this.isEdit) {
      return;
    }
    if (this.workSpaces.length === 0) {
      return;
    }
    this.isSelectOpen = !this.isSelectOpen;
  }

  private generateLoadingForm(): VehicleLoadingForm {
    return new UntypedFormGroup({
      palletVehicleToLoad: new UntypedFormControl(null),
      palletVehicle: new UntypedFormControl(null),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.isEdit && changes.isEdit.currentValue && this.cargoSpaces?.length > 0) {
      this.form.enable();
    }
    if (changes && changes.isEdit && !changes.isEdit.currentValue) {
      this.form.disable();
    }

    if (changes.chosenLoadingSpace && changes.chosenLoadingSpace.currentValue) {
      const palletVehicleToLoad = changes.chosenLoadingSpace.currentValue
        .palletVehicleToLoad as boolean;
      const palletVehicle = changes.chosenLoadingSpace.currentValue.palletVehicle as number;
      this.form.patchValue({
        palletVehicleToLoad: palletVehicleToLoad !== undefined ? palletVehicleToLoad : null,
        palletVehicle: palletVehicle !== undefined ? palletVehicle : null,
      });
      this.choosenWorkspace = this.chosenLoadingSpace;
      this.palletVehicleCurrentValue = palletVehicle;
    }
  }

  ngOnInit(): void {
    this.formSubscription = this.form.valueChanges.subscribe(() => {
      this.submitEvent.emit(this.form.value);
    });
    const palletVehicleToLoad = this.loadingSpace.palletVehicleToLoad;
    const palletVehicle = this.loadingSpace.palletVehicle ? this.loadingSpace.palletVehicle : null;
    this.form.patchValue({
      palletVehicleToLoad: palletVehicleToLoad !== undefined ? palletVehicleToLoad : null,
      palletVehicle: palletVehicle !== undefined ? palletVehicle : null,
    });
    if (this.loadingSpace.palletVehicle) {
      this.choosenWorkspace = this.workSpaces.find(
        (workspace) => workspace.id === this.loadingSpace.palletVehicle,
      );
    } else {
      this.choosenWorkspace = this.chosenLoadingSpace;
    }
    this.palletVehicleCurrentValue = palletVehicle;
  }

  ngOnDestroy(): void {
    this.submitEvent.emit(this.form.value);
    this.formSubscription.unsubscribe();
  }
}
